import { CdkTrapFocus } from '@angular/cdk/a11y';
import { DatePipe } from '@angular/common';
import { AfterViewInit, Component, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { FormArray, ReactiveFormsModule } from '@angular/forms';
import { ScoreDetailService } from '@core/score-detail.service';
import { IPrintableReportHeading } from '@models/report-print-heading';
import { DeleteScoresConfirmationResponse } from '@models/score';
import { WordListLevel } from '@models/word-list-level';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import {
  AssessmentStatus
} from '@shared/branded-reports/branded-individual-student-over-time-report-detail/student-over-time-report-detail.model';
import { ButtonComponent } from '@shared/button/button.component';
import { DateHelpers } from '@shared/date-helpers';
import { ProductType } from '@shared/enums/product-type';
import { ScoreBenchmark } from '@shared/enums/score-benchmark';
import { TopicType } from '@shared/enums/topic-type';
import { GenericConfirmModalComponent } from '@shared/modals/generic-confirm-modal/generic-confirm-modal.component';
import { ModalTemplateComponent } from '@shared/modals/modal-template/modal-template.component';
import { PreloaderModule } from '@shared/preloader/preloader.module';
import { ScoreDetailViewComponent } from '@shared/score-detail/score-detail-view/score-detail-view.component';
import { ToggleSwitchComponent } from '@shared/toggle-switch/toggle-switch.component';
import { ZbpDataTable } from '@shared/zbp-data-table/zbp-data-table.model';
import { zbpIconNames } from '@shared/zbp-icon/zbp-icon-names';
import { ToastrService } from 'ngx-toastr';
import { catchError, firstValueFrom, from, Observable, of, Subscription, tap } from 'rxjs';

import { finalize } from 'rxjs/operators';
import {
  DeleteScoresConfirmationParams,
  IScoreDetailActivityModalParams,
  IScoreDetailAssessmentModalParams
} from './score-detail-params';

@Component({
  selector: 'zbp-score-detail-modal',
  templateUrl: './score-detail-modal.component.html',
  styleUrls: ['./score-detail-modal.component.scss'],
  imports: [
    CdkTrapFocus,
    ButtonComponent,
    ReactiveFormsModule,
    ModalTemplateComponent,
    PreloaderModule,
    ScoreDetailViewComponent,
    PreloaderModule,
    TranslateModule,
    ToggleSwitchComponent,
  ],
  providers: [
    DatePipe
  ]
})
export class ScoreDetailModalComponent implements AfterViewInit, OnDestroy {
  // Common variables
  studentName: string = '';
  className: string = '';
  productName: string = '';
  activityName: string = '';
  score: string = '';
  topicType: TopicType;
  timeSpent?: string;
  wordListLevel?: string;

  // Activity variables
  /**
   * This is used for student activity reports.
   *
   * @todo provide the union type, ScoreDetail, and then change conditionals to check based on the specific class
   *       instead of relying on "any" type. ZBP-1872.
   */
  scoreDetail: any;
  benchmark: ScoreBenchmark;

  // Assessment variables
  /**
   * This is used for progress reports.
   *
   * Note that the term "part" is not the same model as described in score.ts.
   *
   * @todo provide the correct type for this rather than "any" type. ZBP-1872.
   */
  parts: any[] | undefined;
  productType: ProductType;
  // @todo determine if VariantType or string is the correct type instead of "any". ZBP-1872.
  variant: any;
  classroomId: string;
  studentId: string;
  assessmentKey: string;
  /**
   * Whether manual scoring should be used on initial load.
   *
   * This is described as whether the score has already been overridden or the status is AddScore.
   */
  manualScore = false;
  scoreIsOverridden = false;
  assessmentStatus: AssessmentStatus;

  columns: ZbpDataTable.Column[] = [];
  rows: ZbpDataTable.Row[] = [];

  zbpIconNames = zbpIconNames;

  addSubFormControls = new FormArray([]);

  manualScoreResponse: any;

  allowEdit: boolean;
  completedOn: string;
  showCompletedOn: boolean = false;
  lastOverriddenUserName: string;
  lastOverriddenOn: string;
  showLastOverriddenMessage: boolean = false;

  /** InProgress and NotStarted cannot be edited */
  editableStatuses = [AssessmentStatus.Completed, AssessmentStatus.AddScore, AssessmentStatus.PartiallyScored];

  isSaving = false;
  isEditing = false;
  isDeleting = false; // used to determine if the dismiss button should refresh the report
  hasUnscoredManualSection = false;
  isLoading = false;
  printStandards: boolean = true;
  toggleLabel: string;
  deleteScoresInfo: DeleteScoresConfirmationResponse[];

  subscriptions = new Subscription();

  // Printing
  /** Headers that are sent to the printable header component. */
  printableHeaders: IPrintableReportHeading[] = [];

  @ViewChild(ScoreDetailViewComponent) scoreDetailView?: ScoreDetailViewComponent;
  @ViewChild('standardsPrintOptionModalBody') standardsPrintOption!: TemplateRef<any>;
  @ViewChild('deleteScoresConfirmationModalBody') deleteScoresConfirmationRef!: TemplateRef<any>;

  constructor(
    private activeModal: NgbActiveModal,
    private modalService: NgbModal,
    private scoreDetailService: ScoreDetailService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private toastr: ToastrService,
  ) {
  }

  ngAfterViewInit(): void {
    // This needs to be added and removed manually because afterprint also exists on branded progress reports
    window.addEventListener('afterprint', this.afterPrint);
  }

  ngOnDestroy(): void {
    window.removeEventListener('afterprint', this.afterPrint);
    this.subscriptions.unsubscribe();
  }

  // This needs to be a property to keep context
  afterPrint = () => {
    // Restore original states using the data table's method
    this.scoreDetailView?.restoreCollapsibleRowStates();
  };

  public static async openActivity(ngbModal: NgbModal, params: IScoreDetailActivityModalParams): Promise<NgbModalRef> {
    // eslint-disable-next-line prefer-const
    let component: ScoreDetailModalComponent;

    const modalRef = ngbModal.open(
      ScoreDetailModalComponent,
      {
        ...params?.options,
        size: 'xl',
        scrollable: true,
        beforeDismiss: () => (component?.hasUnsavedChanges()
          ? component?.openConfirmModal(ngbModal)
          : true),
      },
    );

    // Common
    component = modalRef.componentInstance as ScoreDetailModalComponent;
    component.studentName = params.studentName;
    component.className = params.className;
    component.productName = params.productName;
    component.activityName = params.activityName;
    component.score = params.score;
    component.topicType = params.topicType;
    component.timeSpent = params.timeSpent;
    component.wordListLevel = params.wordListLevel;

    // Activity
    component.scoreDetail = params.scoreDetail;
    component.benchmark = component.scoreDetail.benchmark;

    component.buildTable();
    return modalRef;
  }

  public static async openAssessment(
    ngbModal: NgbModal, params: IScoreDetailAssessmentModalParams
  ): Promise<NgbModalRef> {
    // eslint-disable-next-line prefer-const
    let component: ScoreDetailModalComponent;

    const modalRef = ngbModal.open(
      ScoreDetailModalComponent,
      {
        ...params?.options,
        size: 'xl',
        scrollable: true,
        beforeDismiss: () => (component?.hasUnsavedChanges()
          ? component?.openConfirmModal(ngbModal)
          : true),
      },
    );

    // Common
    component = modalRef.componentInstance as ScoreDetailModalComponent;
    component.studentName = params.studentName;
    component.className = params.className;
    component.productName = params.productName;
    component.activityName = params.activityName;
    component.score = params.score;
    component.topicType = params.topicType;
    component.wordListLevel = params.wordListLevel;

    component.parts = params.parts;
    component.benchmark = params.benchmark;
    component.productType = params.productType;
    component.variant = params.variant;
    component.classroomId = params.classroomId;
    component.studentId = params.studentId;
    component.assessmentKey = params.assessmentKey;
    component.assessmentStatus = params.assessmentStatus;
    component.scoreIsOverridden = params.scoreIsOverridden;
    component.manualScore = params.scoreIsOverridden
      || params.assessmentStatus === AssessmentStatus.AddScore
      || params.assessmentStatus === AssessmentStatus.PartiallyScored
      || params.assessmentStatus === AssessmentStatus.ReadyForScoring;

    if (params.assessmentStatus === AssessmentStatus.AddScore
      || params.assessmentStatus === AssessmentStatus.ReadyForScoring) {
      component.isEditing = true;
    }

    if (component.manualScore) {
      component.isLoading = true;
      await firstValueFrom(component.getAssessmentManualScore());
      component.isLoading = false;
    }

    component.allowEdit = params.allowEdit;

    component.completedOn = params.completedOn;
    component.showCompletedOn = true;

    component.lastOverriddenUserName = params.lastOverriddenUserName;
    component.lastOverriddenOn = params.lastOverriddenOn;

    component.showLastOverriddenMessage = !!component.lastOverriddenUserName;

    component.wordListLevel = params.wordListLevel;

    component.buildTable();
    return modalRef;
  }

  static translateWordListLevel(wordListLevel: string, translateService: TranslateService): string {
    if (!wordListLevel) {
      return null;
    } else if (wordListLevel === WordListLevel.onLevel) {
      return translateService.instant('shared-benchmark-label-on-level');
    } else if (wordListLevel === WordListLevel.challenge) {
      return translateService.instant('shared-benchmark-label-challenge');
    } else if (wordListLevel === WordListLevel.emerging) {
      return translateService.instant('shared-benchmark-label-emerging');
    }
    return null;
  }

  buildTable(): void {
    const vm = this.scoreDetailService.buildTable({
      topicType: this.topicType,
      score: this.score,
      benchmark: this.benchmark,
      scoreDetail: this.scoreDetail,
      parts: this.parts,
      manualScoreResponse: this.manualScoreResponse,
      isEditing: this.isEditing,
      productType: this.productType,
      timeSpent: this.timeSpent,
    });

    this.columns = vm.columns;
    this.rows = vm.rows;
    this.hasUnscoredManualSection = vm.hasUnscoredManualSection;

    // Populate addSubFormControls with form groups created by the service
    if (this.parts && vm.formGroups) {
      this.addSubFormControls.clear();
      vm.formGroups.forEach((group) => {
        this.addSubFormControls.push(group);
      });
    }
  }

  hasUnsavedChanges(): boolean {
    return this.addSubFormControls.dirty;
  }

  openConfirmModal(ngbModal: NgbModal): Promise<any> {
    const confirmCloseModalRef = GenericConfirmModalComponent.open(ngbModal, {
      title: '',
      body: 'You have unsaved changes, please click confirm to continue.',
    });
    return confirmCloseModalRef.result;
  }

  dismiss(): void {
    if (this.isDeleting) {
      this.activeModal.close({ refreshData: true });
    } else {
      this.activeModal.dismiss();
    }
  }

  save(): void {
    const dirtyFields = this.addSubFormControls.controls.filter(item => item.dirty);

    const values = dirtyFields.map((value) => {
      const correctValue = value.value;
      if (correctValue.totalCorrect === null) {
        return { part: correctValue.part, deleteScore: true };
      }

      return correctValue;
    });

    if (values.length > 0) {
      this.subscriptions.add(
        this.setManualScore(values).subscribe((result) => {
          if (result.success) {
            this.activeModal.close({ refreshData: true });
          }
        })
      );
    }
  }

  /**
   * Opens confirmation modal informing teachers this action is irreversible
   * Translations not needed as this won't be an option for parents
   */
  enableEditMode(): void {
    const confirmModalRef = GenericConfirmModalComponent.open(this.modalService, {
      title: 'Are you sure you want to proceed?',
      body: `Manually overriding this student's score will overwrite their previously recorded responses
        for the impacted parts of this test. <strong>This action is irreversible.</strong>  Do you want to continue?`,
      confirm: { label: 'Confirm' },
      options: { size: 'med' }
    });

    const confirm$ = from(confirmModalRef.result).pipe(
      catchError(() => of(false))
    );

    this.subscriptions.add(
      confirm$.subscribe((response) => {
        const userConfirmed = response?.confirm === true;
        if (!userConfirmed) {
          return;
        }

        this.isEditing = true;
        if (!this.manualScoreResponse) {
          // Need to wait for observable
          this.getAssessmentManualScore().subscribe(() => {
            this.buildTable();
          });
        } else {
          this.buildTable();
        }
      })
    );
  }

  /**
   * Presents user with information about the delete scores action and a table including the current
   * score and most recent score (if available).
   */
  deleteScores(): void {
    const params: DeleteScoresConfirmationParams = {
      assessmentKey: this.assessmentKey,
      includeLogicallyDeleted: false, // returns only active scores
      productType: this.productType,
      userId: this.studentId,
      pageNumber: 1,
      pageSize: 2
    }
    this.subscriptions.add(
      this.scoreDetailService.getDeleteScoresConfirmation(params).subscribe((res) => {
        this.deleteScoresInfo = res.response;
        this.deleteScoresInfo.forEach((value) => {
          value.completedOn = this.datePipe.transform(value.completedOn, DateHelpers.DATE_AND_TIME_FORMAT);
          value.createdOn = this.datePipe.transform(value.createdOn, DateHelpers.DATE_AND_TIME_FORMAT);
        });
        const confirmModalRef = GenericConfirmModalComponent.open(this.modalService, {
          title: 'Are you sure you want to proceed?',
          bodyRef: this.deleteScoresConfirmationRef,
          confirm: { label: 'Confirm' },
          cancel: { label: 'Cancel' },
          options: { size: 'lg' }
        });

        const confirm$ = from(confirmModalRef.result).pipe(
          catchError(() => of(false))
        );

        this.subscriptions.add(
          confirm$.subscribe((response) => {
            const userConfirmed = response?.confirm === true;
            if (!userConfirmed) {
              return;
            }
            this.isDeleting = true;
            this.deleteCurrentScore().subscribe(
              {
                next: () => this.buildTable(),
                error: (err) => {
                  this.toastr.error('Unable to delete current score.');
                  console.error('Delete current score failed:', err);
                }
              });
          })
        );
      })
    );
  }

  deleteCurrentScore(): Observable<any> {
    const scoreToDelete = this.deleteScoresInfo?.find(
      score => score.assessmentAvailableDescription === 'Current');

    if (!scoreToDelete) {
      this.isDeleting = false;
      throw new Error('Unable to delete current score');
    }

    return this.scoreDetailService.deleteCurrentScore(scoreToDelete.activityId).pipe(
      tap((response) => {
        // if length is > 1 then when we delete there will be scores to display
        if (this.deleteScoresInfo.length > 1) {
          this.manualScoreResponse = null;
          this.score = response.score;
          this.benchmark = response.benchmark;
          this.scoreDetail = response.scoreDetail;
          this.parts = response.scoreDetail;
          this.isEditing = false;
          this.timeSpent = response.timeSpent;
          this.wordListLevel = ScoreDetailModalComponent.translateWordListLevel(response.wordListLevel, this.translate);
          this.showLastOverriddenMessage = !!response.lastOverriddenOn;
          this.lastOverriddenUserName = response.lastOverriddenUserName;
          this.lastOverriddenOn = response.lastOverriddenOn;
        } else {
          // otherwise, we need to reset everything so it appears the student never took the assessment
          this.manualScoreResponse = null;
          this.score = `${response.totalCorrect}/${response.totalQuestions}`;
          this.benchmark = ScoreBenchmark.NotScored;
          this.scoreDetail = response.scoreDetail;
          this.isEditing = true;
          this.timeSpent = null;
          this.wordListLevel = null;
          this.showLastOverriddenMessage = false;
          this.lastOverriddenUserName = null;
          this.lastOverriddenOn = null;

          this.updatePartsWhenDeleted();
        }
      }),
    );
  }

  /**
   * Updates the current parts to reflect not scored when deleting the last score
   */
  updatePartsWhenDeleted(): void {
    if (!this.parts || this.parts.length === 0) {
      return;
    }

    this.parts.forEach((part) => {
      part.benchmark = 'NotScored';
      part.score = '';
      part.totalCorrect = 0;
      part.questions?.forEach((question) => {
        question.attemptLink = null;
        question.attempts = [];
      })
    })
  }

  getAssessmentManualScore(): Observable<any> {
    this.isSaving = true;
    return this.scoreDetailService.getAssessmentManualScore({
      assessmentKey: this.assessmentKey,
      productType: this.productType,
      variant: this.variant,
      classroomId: this.classroomId,
      studentId: this.studentId,
    }).pipe(
      finalize(() => {
        this.isSaving = false;
      }),
      tap((response) => {
        this.manualScoreResponse = response;
      }),
    );
  }

  private setManualScore(data: { part: string; totalCorrect: number }[]): Observable<any> {
    this.isSaving = true;
    return this.scoreDetailService.setManualScore({
      assessmentKey: this.assessmentKey,
      productType: this.productType,
      variant: this.variant,
      classroomId: this.classroomId,
      studentId: this.studentId,
      data,
    }).pipe(
      finalize(() => {
        this.isSaving = false;
      }),
    );
  }

  print(): void {
    this.getPrintHeaders();

    if (this.showStandardsPrintOptionsModal()) {
      this.openStandardsPrintOptionsModal();
    } else {
      this.scoreDetailView?.expandAllCollapsibleRows();
      // Wait for DOM updates and then print
      setTimeout(() => {
        window.print();
      }, 100);
    }
  }

  showStandardsPrintOptionsModal(): boolean {
    return this.parts?.some(part => part.questions.some(question => question.questionStandards
        && question.questionStandards.length > 0)
    );
  }

  /**
   * Opens the standards print option modal
   * Defaults to true and translates based on language setting
   * Modal body includes a toggle to drive the printStandards value
   */
  openStandardsPrintOptionsModal(): void {
    this.printStandards = true;
    this.toggleLabel = this.translate.instant('score-detail-include-text-toggle');
    const confirmModalRef = GenericConfirmModalComponent.open(this.modalService, {
      title: this.translate.instant('score-detail-print-standards-header'),
      bodyRef: this.standardsPrintOption,
      cancel: { label: this.translate.instant('shared-button-cancel') },
      confirm: { label: this.translate.instant('shared-button-print') },
      options: { size: 'med' }
    });

    const confirm$ = from(confirmModalRef.result).pipe(
      catchError(() => of(false))
    );

    this.subscriptions.add(
      confirm$.subscribe((response) => {
        const userConfirmed = response?.confirm === true;
        if (!userConfirmed) {
          return;
        }

        // sends boolean to determine if the secondary rows should be expanded
        this.scoreDetailView?.expandAllCollapsibleRows(this.printStandards);

        // Wait for DOM updates and then print
        setTimeout(() => {
          window.print();
        }, 100);
      })
    );
  }

  private getPrintHeaders(): void {
    this.printableHeaders = [
      { // Student
        label: this.translate.instant('shared-score-detail-field-student'),
        value: this.studentName ?? ''
      },
      { // Class
        label: this.translate.instant('shared-score-detail-field-class'),
        value: this.className
          ? this.className
          : this.translate.instant('parent-report-all-classes')
      },
      { // Report
        label: this.translate.instant('parent-report-name-field'),
        value: this.translate.instant('shared-score-detail-header')
      },
      { // Product
        label: this.translate.instant('parent-report-product-field'),
        value: this.productName
          ? this.productName
          : this.translate.instant('parent-report-all-products')
      },
      { // Activity
        label: this.translate.instant('shared-student-activity-report-activity'),
        value: this.activityName ?? ''
      },
    ];
    if (this.wordListLevel) {
      this.printableHeaders.push(
        { // word list level
          label: this.translate.instant('parent-report-wordList-field'),
          value: this.wordListLevel,
        }
      )
    }
    if (this.showCompletedOn) {
      this.printableHeaders.push(
        { // Date/Time Completed
          label: this.translate.instant('shared-score-detail-field-date-time-completed'),
          value: this.completedOn
            ? this.datePipe.transform(this.completedOn, DateHelpers.DATE_AND_TIME_FORMAT)
            : this.translate.instant('shared-score-detail-field-date-time-completed-empty-message')
        }
      );
    }
  }
}
